
import { GameState, createInitialGameState, type Piece, type PieceType, type PieceColor, type CastlingRights } from './chess-engine';

export function fenToGameState(fen: string): GameState {
  const parts = fen.split(' ');
  if (parts.length < 4) {
    console.warn('Invalid FEN, using initial position');
    return createInitialGameState();
  }

  const [boardPart, sideToMove, castling, enPassant, halfmove = '0', fullmove = '1'] = parts;

  // Parse board
  const board: Piece[][] = Array(8).fill(null).map(() => Array(8).fill(null));
  const rows = boardPart.split('/');
  
  for (let row = 0; row < 8; row++) {
    let col = 0;
    for (const char of rows[row]) {
      if (char >= '1' && char <= '8') {
        // Empty squares
        col += parseInt(char);
      } else {
        // Piece
        const piece = charToPiece(char);
        if (piece && col < 8) {
          board[row][col] = piece;
        }
        col++;
      }
    }
  }

  // Parse castling rights
  const castlingRights: CastlingRights = {
    whiteKingSide: castling.includes('K'),
    whiteQueenSide: castling.includes('Q'),
    blackKingSide: castling.includes('k'),
    blackQueenSide: castling.includes('q'),
  };

  // Parse en passant
  let enPassantTarget: [number, number] | null = null;
  if (enPassant !== '-') {
    const col = enPassant.charCodeAt(0) - 97; // 'a' = 0
    const row = 8 - parseInt(enPassant[1]); // '8' = 0, '1' = 7
    if (row >= 0 && row < 8 && col >= 0 && col < 8) {
      enPassantTarget = [row, col];
    }
  }

  return {
    board,
    sideToMove: sideToMove === 'w' ? 'white' : 'black',
    castlingRights,
    enPassantTarget,
    halfmoveClock: parseInt(halfmove),
    fullmoveNumber: parseInt(fullmove),
    moveHistory: [],
    positionHistory: new Map(),
  };
}

function charToPiece(char: string): Piece {
  if (!char || char === ' ') return null;
  
  const pieceMap: Record<string, PieceType> = {
    'p': 'P', 'r': 'R', 'n': 'N', 'b': 'B', 'q': 'Q', 'k': 'K',
    'P': 'P', 'R': 'R', 'N': 'N', 'B': 'B', 'Q': 'Q', 'K': 'K'
  };
  
  const pieceType = pieceMap[char];
  if (!pieceType) return null;
  
  const color: PieceColor = char === char.toUpperCase() ? 'white' : 'black';
  
  return {
    type: pieceType,
    color
  };
}

export function gameStateToFen(gameState: GameState): string {
  // Convert board to FEN notation
  let fenBoard = '';
  for (let row = 0; row < 8; row++) {
    let emptyCount = 0;
    for (let col = 0; col < 8; col++) {
      const piece = gameState.board[row][col];
      if (piece) {
        if (emptyCount > 0) {
          fenBoard += emptyCount;
          emptyCount = 0;
        }
        let pieceChar = piece.type.toLowerCase();
        if (piece.color === 'white') {
          pieceChar = pieceChar.toUpperCase();
        }
        fenBoard += pieceChar;
      } else {
        emptyCount++;
      }
    }
    if (emptyCount > 0) {
      fenBoard += emptyCount;
    }
    if (row < 7) fenBoard += '/';
  }

  // Side to move
  const sideToMove = gameState.sideToMove === 'white' ? 'w' : 'b';

  // Castling availability
  let castling = '';
  if (gameState.castlingRights.whiteKingSide) castling += 'K';
  if (gameState.castlingRights.whiteQueenSide) castling += 'Q';
  if (gameState.castlingRights.blackKingSide) castling += 'k';
  if (gameState.castlingRights.blackQueenSide) castling += 'q';
  if (!castling) castling = '-';

  // En passant target
  let enPassant = '-';
  if (gameState.enPassantTarget) {
    const [row, col] = gameState.enPassantTarget;
    enPassant = String.fromCharCode(97 + col) + (8 - row);
  }

  return `${fenBoard} ${sideToMove} ${castling} ${enPassant} ${gameState.halfmoveClock} ${gameState.fullmoveNumber}`;
}
